import { useMemo, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2, X, MessageSquare, User as UserIcon, Bot, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { SearchParam } from '@common/ts/enums';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import useResponsesPanelStore from '@/store/ResponsesPanelStore';
import useTranscriptPanelStore from '@/store/TranscriptPanelStore';
import useMediaQuery from '@/hooks/useMediaQuery';
import useTranslation from '@/hooks/useTranslation';
import { useGetInterviewQuestionTransripts } from '@/reactQuery/get';
import useCategoryStore from '@/store/CategoryStore';
import getTextColor from '@/utils/getTextColorContrast';
import { Transcripts, Category } from '@/types';

type SortOption = 'newest' | 'oldest' | 'respondent';

/** Strip metadata markers such as *** Sent as audio ***, etc. */
function cleanMessageText(text: string): string {
  return text
    .replace(/\*{2,3}\s*Sent as (?:audio|video|image|file)\s*\*{2,3}/gi, '')
    .replace(/\*{2,3}\s*Audio message\s*\*{2,3}/gi, '')
    .replace(/\*{2,3}\s*Video message\s*\*{2,3}/gi, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Extract the first participant answer text from a raw transcript string. */
function extractParticipantAnswer(rawText: string): string {
  const messages = parseMessages(rawText);
  const participant = messages.find(
    (msg) => msg.role !== 'Interviewer' && msg.role !== 'Assistant',
  );
  return participant?.text || cleanMessageText(rawText);
}

/** Parse all messages from a raw transcript string into role/text pairs. */
function parseMessages(rawText: string): Array<{ role: string; text: string }> {
  const messageRegex = /(?:^|\n)((?:Interviewer|Participant|Assistant|User):)\s*/g;
  const messages: { role: string; text: string }[] = [];
  let lastIdx = 0;
  let lastRole = '';
  let m: RegExpExecArray | null;

  // eslint-disable-next-line no-cond-assign
  while ((m = messageRegex.exec(rawText)) !== null) {
    if (lastRole) {
      const cleaned = cleanMessageText(rawText.slice(lastIdx, m.index));
      if (cleaned) messages.push({ role: lastRole, text: cleaned });
    }
    lastRole = m[1].replace(':', '');
    lastIdx = m.index + m[0].length;
  }
  if (lastRole) {
    const cleaned = cleanMessageText(rawText.slice(lastIdx));
    if (cleaned) messages.push({ role: lastRole, text: cleaned });
  }

  return messages.length > 0 ? messages : [{ role: 'Participant', text: cleanMessageText(rawText) }];
}

interface ResponseCardProps {
  transcript: Transcripts;
  index: number;
  onOpenTranscript: (conversationId: string) => void;
}

function ResponseCard({ transcript, index, onOpenTranscript }: ResponseCardProps) {
  const { lang } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const { conversation_id, questions, category, media } = transcript;
  const messages = useMemo(() => parseMessages(questions || ''), [questions]);

  // Preview: first participant message, truncated
  const firstParticipant = messages.find(
    (msg) => msg.role !== 'Interviewer' && msg.role !== 'Assistant',
  );
  const previewText = firstParticipant?.text || '';
  const truncated = previewText.length > 300 ? `${previewText.slice(0, 300)}…` : previewText;
  const hasMultipleMessages = messages.length > 1;

  const categories = Array.isArray(category) ? category : category ? [category] : [];

  return (
    <div className="rounded-xl border border-neutral-200/80 bg-white p-4 flex flex-col gap-3">
      {/* Card header: respondent badge + categories */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => onOpenTranscript(conversation_id)}
          className="flex items-center gap-2 group"
          title={lang.get('msg.openFullTranscript')}
        >
          <div className="flex items-center justify-center h-7 w-7 rounded-full bg-emerald-50 group-hover:bg-emerald-100 transition-colors">
            <UserIcon className="h-3.5 w-3.5 text-emerald-600" />
          </div>
          <span className="text-sm font-semibold text-neutral-700 group-hover:text-neutral-900 transition-colors">
            {lang.get('msg.respondent')} #{index + 1}
          </span>
        </button>

        {categories.length > 0 && (
          <div className="flex flex-wrap gap-1.5 justify-end">
            {categories.map((cat: Category) =>
              cat?.label ? (
                <span
                  key={cat.label}
                  style={{
                    backgroundColor: cat.color,
                    color: getTextColor(cat.color || ''),
                  }}
                  className="text-xs font-medium px-2.5 py-0.5 rounded-full"
                >
                  {cat.label}
                </span>
              ) : null,
            )}
          </div>
        )}
      </div>

      {/* Collapsed: preview of first answer */}
      {!isExpanded && (
        <p className="text-sm leading-relaxed text-neutral-700">{truncated}</p>
      )}

      {/* Expanded: full Q&A back-and-forth */}
      {isExpanded && (
        <div className="flex flex-col gap-2">
          {messages.map((msg, idx) => {
            const isInterviewer = msg.role === 'Interviewer' || msg.role === 'Assistant';
            return (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={idx}
                className={`flex gap-2 ${isInterviewer ? '' : 'pl-2'}`}
              >
                <div className={`flex items-center justify-center h-5 w-5 rounded-full shrink-0 mt-0.5 ${isInterviewer ? 'bg-neutral-100' : 'bg-emerald-50'}`}>
                  {isInterviewer ? (
                    <Bot className="h-3 w-3 text-neutral-400" />
                  ) : (
                    <UserIcon className="h-3 w-3 text-emerald-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span className={`text-[10px] font-semibold uppercase tracking-wide ${isInterviewer ? 'text-neutral-400' : 'text-emerald-600'}`}>
                    {isInterviewer ? lang.get('msg.interviewer') : lang.get('msg.respondent')}
                  </span>
                  <p className="text-sm leading-relaxed text-neutral-700 mt-0.5">{msg.text}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Expand/collapse toggle */}
      {hasMultipleMessages && (
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-700 transition-colors"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="h-3 w-3" />
              {lang.get('msg.showLess')}
            </>
          ) : (
            <>
              <ChevronDown className="h-3 w-3" />
              {lang.get('msg.showFullConversation')}
            </>
          )}
        </button>
      )}

      {/* Media attachments */}
      {media && media.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {media.map((item, idx) => {
            if (item.type === 'image') {
              return (
                <a
                  // eslint-disable-next-line react/no-array-index-key
                  key={idx}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={item.url}
                    alt={item.label || lang.get('msg.respondentImage')}
                    className="w-full max-w-[200px] rounded-lg object-cover border border-neutral-200 hover:opacity-80 transition-opacity cursor-pointer"
                  />
                </a>
              );
            }
            if (item.type === 'audio') {
              return (
                // eslint-disable-next-line react/no-array-index-key
                <audio key={idx} src={item.url} controls preload="none" className="w-full h-8" />
              );
            }
            if (item.type === 'video') {
              return (
                // eslint-disable-next-line react/no-array-index-key
                <video key={idx} src={item.url} controls preload="metadata" className="w-full rounded-lg max-h-48" />
              );
            }
            return null;
          })}
        </div>
      )}
    </div>
  );
}

function ResponsesPanel() {
  const { lang } = useTranslation();
  const { project_id: projectId } = useParams();
  const [searchParams] = useSearchParams();
  const filterId = searchParams.get(SearchParam.FILTER_ID);
  const categoryType = useCategoryStore((state) => state.categoryType);

  const { isOpen, questionId, selectedCategory, setCategory, close } = useResponsesPanelStore();
  const { open: openTranscriptPanel } = useTranscriptPanelStore();
  // At lg+ both panels can sit side-by-side (their widths add up to
  // < 1024px). Below lg they would overlap, so we close ResponsesPanel
  // before opening TranscriptPanel to avoid a stacked pile of sheets.
  const isLg = useMediaQuery('(min-width: 1024px)');

  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('respondent');
  const [showSortMenu, setShowSortMenu] = useState(false);

  // Reset local state when panel opens with new question
  const handleClose = useCallback(() => {
    close();
    setSearchQuery('');
    setSortBy('respondent');
    setShowSortMenu(false);
  }, [close]);

  const {
    data: transcripts,
    isLoading,
  } = useGetInterviewQuestionTransripts(filterId, categoryType, projectId, questionId ?? undefined);

  // Collect unique category labels for the filter dropdown
  const allCategories = useMemo(() => {
    if (!transcripts) return [];
    const labels = new Set<string>();
    for (const t of transcripts) {
      const cats = Array.isArray(t.category) ? t.category : t.category ? [t.category] : [];
      for (const cat of cats) {
        if (cat?.label) labels.add(cat.label);
      }
    }
    return Array.from(labels).sort();
  }, [transcripts]);

  // Filter by category + search query
  const filteredTranscripts = useMemo(() => {
    if (!transcripts) return [];

    let result = transcripts;

    // Category filter
    if (selectedCategory) {
      const filterLabel = selectedCategory.toLowerCase();
      result = result.filter((transcript) => {
        const cats = Array.isArray(transcript.category)
          ? transcript.category
          : transcript.category
            ? [transcript.category]
            : [];
        return cats.some((cat) => cat?.label?.toLowerCase() === filterLabel);
      });
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((transcript) => {
        const answer = extractParticipantAnswer(transcript.questions || '');
        return answer.toLowerCase().includes(query);
      });
    }

    // Sort
    if (sortBy === 'newest') {
      result = [...result].sort((a, b) =>
        new Date(b.started || 0).getTime() - new Date(a.started || 0).getTime(),
      );
    } else if (sortBy === 'oldest') {
      result = [...result].sort((a, b) =>
        new Date(a.started || 0).getTime() - new Date(b.started || 0).getTime(),
      );
    }
    // 'respondent' keeps the default order

    return result;
  }, [transcripts, selectedCategory, searchQuery, sortBy]);

  const handleOpenTranscript = (conversationId: string) => {
    // Only close ResponsesPanel on narrow widths where both sheets
    // would overlap. At lg+ both panels coexist.
    if (!isLg) {
      handleClose();
    }
    openTranscriptPanel(conversationId);
  };

  const sortLabels: Record<SortOption, string> = {
    newest: lang.get('msg.sortNewestFirst'),
    oldest: lang.get('msg.sortOldestFirst'),
    respondent: lang.get('msg.sortRespondentNumber'),
  };

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <SheetContent
        side="right"
        className="w-full sm:w-[50%] sm:min-w-[500px] max-w-[800px] p-0 flex flex-col"
      >
        <SheetTitle className="sr-only">{lang.get('msg.responsesPanelTitle')}</SheetTitle>

        {/* Header */}
        <div className="border-b border-neutral-200 px-5 py-4 shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-neutral-400" />
              <h2 className="text-sm font-semibold text-neutral-900">{lang.get('msg.responses')}</h2>
              {filteredTranscripts.length > 0 && (
                <span className="text-xs text-neutral-400">
                  ({filteredTranscripts.length}{transcripts && selectedCategory ? ` of ${transcripts.length}` : ''})
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="h-7 w-7 p-0 text-neutral-400 hover:text-neutral-600"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Controls row */}
          <div className="flex items-center gap-2">
            {/* Category filter */}
            <select
              value={selectedCategory || ''}
              onChange={(e) => setCategory(e.target.value || null)}
              className="h-8 text-xs border border-neutral-200 rounded-lg px-2 pr-7 bg-white text-neutral-700 focus:outline-none focus:ring-2 focus:ring-emerald-200 appearance-none truncate max-w-[200px]"
              style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%239ca3af\' stroke-width=\'2\'%3E%3Cpath d=\'m6 9 6 6 6-6\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center' }}
            >
              <option value="">{lang.get('msg.allCategories')}</option>
              {allCategories.map((label) => (
                <option key={label} value={label}>{label}</option>
              ))}
            </select>

            {/* Sort */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowSortMenu(!showSortMenu)}
                className="h-8 text-xs border border-neutral-200 rounded-lg px-3 bg-white text-neutral-700 hover:bg-neutral-50 flex items-center gap-1.5"
              >
                {sortLabels[sortBy]}
                <ChevronDown className="h-3 w-3 text-neutral-400" />
              </button>
              {showSortMenu && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg z-10 py-1 min-w-[140px]">
                  {(Object.entries(sortLabels) as [SortOption, string][]).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => { setSortBy(key); setShowSortMenu(false); }}
                      className={`w-full text-left px-3 py-1.5 text-xs hover:bg-neutral-50 ${sortBy === key ? 'text-emerald-600 font-medium' : 'text-neutral-700'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={lang.get('msg.searchResponses')}
                className="w-full h-8 text-xs border border-neutral-200 rounded-lg pl-8 pr-3 bg-white text-neutral-700 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-emerald-200"
              />
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
            </div>
          ) : filteredTranscripts.length === 0 ? (
            <p className="text-sm text-neutral-400 text-center py-8">{lang.get('msg.noResponsesFound')}</p>
          ) : (
            <div className="flex flex-col gap-3">
              {filteredTranscripts.map((transcript, index) => (
                <ResponseCard
                  key={transcript.conversation_id}
                  transcript={transcript}
                  index={index}
                  onOpenTranscript={handleOpenTranscript}
                />
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default ResponsesPanel;
